import { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Bot } from 'lucide-react';
import { cn } from '@/lib/cn';
import { env } from '@/lib/env';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const WELCOME: Message = {
  role: 'assistant',
  content: "Hi! I'm Axel, Advoltix's support assistant. Ask me anything about the platform, pricing, or integrations — I'm here to help.",
};

async function sendMessage(messages: Message[]): Promise<string> {
  try {
    const res = await fetch(`${env.apiBaseUrl}/api/v1/support/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: messages.map(m => ({ role: m.role, content: m.content })),
      }),
    });
    const data = await res.json() as { reply: string };
    return data.reply;
  } catch {
    return "I'm having trouble connecting right now. You can reach the team directly at support@advoltix.com";
  }
}

/** Renders message text — makes email addresses clickable mailto links */
function MessageText({ text }: { text: string }) {
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(emailRegex);

  return (
    <>
      {parts.map((part, i) =>
        emailRegex.test(part) ? (
          <a
            key={i}
            href={`mailto:${part}`}
            className="font-semibold text-accent-300 underline underline-offset-2 hover:text-accent-200"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const submit = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const next: Message[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);

    try {
      const reply = await sendMessage(next);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Floating trigger ── */}
      <motion.button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-white shadow-glow hover:bg-accent-400"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? 'Close support chat' : 'Open support chat'}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="h-5 w-5" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <MessageCircle className="h-5 w-5" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* ── Chat panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl"
            style={{
              background: 'var(--ui-topbar)',
              border: '1px solid var(--ui-border)',
              boxShadow: '0 24px_80px rgba(4,3,8,0.5), 0 0 0 1px rgba(99,102,241,0.10)',
              maxHeight: '520px',
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--ui-border)' }}>
              <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-accent-500/15">
                <Bot className="h-4 w-4 text-accent-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Axel — Support</p>
                <div className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span className="font-mono text-[10px] text-white/40">Online · AI-powered</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4" style={{ minHeight: 0, maxHeight: '340px' }}>
              <div className="flex flex-col gap-3">
                {messages.map((msg, i) => (
                  <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    <div
                      className={cn(
                        'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                        msg.role === 'user'
                          ? 'bg-accent-500 text-white'
                          : 'border border-lp bg-lp-card text-white'
                      )}
                    >
                      <MessageText text={msg.content} />
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 rounded-2xl border border-lp bg-lp-card px-4 py-2.5 text-sm text-white/50">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-accent-400" />
                      Axel is typing…
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>
            </div>

            {/* Input */}
            <div className="px-4 py-3" style={{ borderTop: '1px solid var(--ui-border)' }}>
              <div className="flex items-center gap-2 rounded-2xl border border-lp bg-lp-card px-4 py-2.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit()}
                  placeholder="Ask anything…"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={submit}
                  disabled={!input.trim() || loading}
                  className="flex h-7 w-7 flex-none items-center justify-center rounded-xl bg-accent-500 text-white transition-all hover:bg-accent-400 disabled:opacity-40"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-1.5 text-center font-mono text-[9px] text-white/20">
                Powered by Advoltix AI
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
